import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    TextField,
    List,
    ListItem,
    ListItemText,
    Typography,
    Chip,
    makeStyles
} from "@material-ui/core";
import { Event as EventIcon, Schedule as ScheduleIcon } from "@material-ui/icons";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { createSchedule, listSchedules } from "../../services/funnelOpportunitySchedules";

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(2)
    },
    scheduleItem: {
        marginBottom: theme.spacing(1),
        backgroundColor: theme.palette.background.default,
        borderRadius: 8,
        padding: theme.spacing(1.5)
    },
    scheduleDate: {
        fontSize: "0.85rem",
        fontWeight: 600,
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(0.5)
    },
    scheduleMessage: {
        fontSize: "0.9rem",
        marginBottom: theme.spacing(0.5)
    },
    scheduleUser: {
        fontSize: "0.75rem",
        color: theme.palette.text.secondary,
        fontStyle: "italic"
    },
    statusChip: {
        marginTop: theme.spacing(0.5)
    },
    formBox: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
        borderRadius: 8
    },
    dateTimeRow: {
        display: "flex",
        gap: theme.spacing(2),
        marginTop: theme.spacing(2)
    },
    emptyState: {
        textAlign: "center",
        padding: theme.spacing(4),
        color: theme.palette.text.secondary
    }
}));

const SchedulingTab = ({ opportunity, ticket }) => {
    const classes = useStyles();
    const [schedules, setSchedules] = useState([]);
    const [showAddSchedule, setShowAddSchedule] = useState(false);
    const [newMessage, setNewMessage] = useState("");
    const [scheduledDate, setScheduledDate] = useState("");
    const [scheduledTime, setScheduledTime] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (opportunity || ticket) {
            fetchSchedules();
        }
    }, [opportunity, ticket]);

    const fetchSchedules = async () => {
        try {
            if (opportunity) {
                const { data } = await listSchedules(opportunity.id);
                setSchedules(data);
            } else if (ticket?.contact?.id) {
                // Buscar agendamentos por contactId quando não há prospecção
                const { data } = await listSchedules(null, ticket.contact.id);
                setSchedules(data);
            }
        } catch (err) {
            toastError(err);
        }
    };

    const handleCreateSchedule = async () => {
        if (!newMessage.trim()) {
            toast.error("Digite uma mensagem");
            return;
        }

        if (!scheduledDate || !scheduledTime) {
            toast.error("Selecione data e hora");
            return;
        }

        const scheduledDateObj = new Date(`${scheduledDate}T${scheduledTime}:00`);
        const scheduledFor = scheduledDateObj.toISOString();

        setLoading(true);
        try {
            if (opportunity) {
                await createSchedule(opportunity.id, {
                    message: newMessage.trim(),
                    scheduledFor
                });
            } else if (ticket?.contact?.id) {
                // Criar agendamento por contactId quando não há prospecção
                await createSchedule(null, {
                    message: newMessage.trim(),
                    scheduledFor,
                    contactId: ticket.contact.id,
                    whatsappId: ticket.whatsappId
                });
            }
            toast.success("Agendamento criado com sucesso!");
            setNewMessage("");
            setScheduledDate("");
            setScheduledTime("");
            setShowAddSchedule(false);
            fetchSchedules();
        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (date) => {
        try {
            return format(new Date(date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
        } catch {
            return "";
        }
    };

    const getStatusLabel = (status) => {
        const labels = {
            pending: "Pendente",
            sent: "Enviado",
            failed: "Falhou",
            cancelled: "Cancelado"
        };
        return labels[status] || status;
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: "primary",
            sent: "default",
            failed: "secondary",
            cancelled: "default"
        };
        return colors[status] || "default";
    };

    return (
        <Box className={classes.container}>
            {schedules.length === 0 && !showAddSchedule ? (
                <Box className={classes.emptyState}>
                    <EventIcon style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
                    <Typography>Nenhum agendamento registrado</Typography>
                </Box>
            ) : (
                <List>
                    {schedules.map((schedule) => (
                        <ListItem key={schedule.id} className={classes.scheduleItem}>
                            <ListItemText
                                primary={
                                    <>
                                        <Typography className={classes.scheduleDate}>
                                            <ScheduleIcon fontSize="small" style={{ verticalAlign: "middle", marginRight: 4 }} />
                                            {formatDateTime(schedule.scheduledFor)}
                                        </Typography>
                                        <Typography className={classes.scheduleMessage}>
                                            {schedule.message}
                                        </Typography>
                                        <Chip
                                            label={getStatusLabel(schedule.status)}
                                            size="small"
                                            color={getStatusColor(schedule.status)}
                                            className={classes.statusChip}
                                        />
                                    </>
                                }
                                secondary={
                                    <Typography className={classes.scheduleUser}>
                                        Agendado por: {schedule.user?.name || "Sistema"}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            )}

            {showAddSchedule ? (
                <Box className={classes.formBox}>
                    <Typography variant="subtitle2" gutterBottom>
                        Novo Agendamento
                    </Typography>
                    <TextField
                        label="Mensagem"
                        multiline
                        rows={3}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        variant="outlined"
                        fullWidth
                        margin="dense"
                        placeholder="Digite a mensagem que será enviada..."
                    />
                    <Box className={classes.dateTimeRow}>
                        <TextField
                            label="Data"
                            type="date"
                            value={scheduledDate}
                            onChange={(e) => setScheduledDate(e.target.value)}
                            variant="outlined"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Hora"
                            type="time"
                            value={scheduledTime}
                            onChange={(e) => setScheduledTime(e.target.value)}
                            variant="outlined"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>
                    <Box mt={2} display="flex" gap={1} justifyContent="flex-end">
                        <Button
                            onClick={() => {
                                setShowAddSchedule(false);
                                setNewMessage("");
                                setScheduledDate("");
                                setScheduledTime("");
                            }}
                            color="secondary"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleCreateSchedule}
                            color="primary"
                            variant="contained"
                            disabled={loading}
                        >
                            Agendar
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Box mt={2} display="flex" justifyContent="center">
                    <Button
                        startIcon={<EventIcon />}
                        onClick={() => setShowAddSchedule(true)}
                        color="primary"
                        variant="outlined"
                    >
                        Agendar Contato
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default SchedulingTab;
